import React, { useState } from "react";
import {
  fetchFeedback,
  fetchFeedbackThread,
  replyToFeedback,
  updateFeedbackStatus,
  FeedbackThread,
  FeedbackStatus,
} from "../API/Feedback/FeedbackAPI";
import BackButton from "../shared/BackButton/BackButton";
import styles from "./Feedback.module.css";

type FilterValue = "new" | "reviewed" | "resolved" | "awaitingReply";

const FILTERS: { value: FilterValue; label: string }[] = [
  { value: "new", label: "New" },
  { value: "reviewed", label: "Reviewed" },
  { value: "resolved", label: "Resolved" },
  { value: "awaitingReply", label: "Awaiting Reply" },
];

const STATUS_OPTIONS: { value: FeedbackStatus; label: string }[] = [
  { value: "new", label: "Mark new" },
  { value: "reviewed", label: "Mark reviewed" },
  { value: "resolved", label: "Mark resolved" },
];

const formatDate = (iso: string): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString();
};

const getLastMessagePreview = (thread: FeedbackThread): string => {
  if (!thread.messages || thread.messages.length === 0) return "No messages yet.";
  const last = thread.messages[thread.messages.length - 1];
  const text = last.text || "";
  return text.length > 140 ? `${text.slice(0, 140)}…` : text;
};

const getStatusClass = (status: FeedbackStatus): string => {
  switch (status) {
    case "new":
      return styles.statusNew;
    case "reviewed":
      return styles.statusReviewed;
    case "resolved":
      return styles.statusResolved;
    default:
      return "";
  }
};

const Feedback: React.FC = () => {
  const [filter, setFilter] = useState<FilterValue>("new");
  const [threads, setThreads] = useState<FeedbackThread[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);

  const [selectedThread, setSelectedThread] = useState<FeedbackThread | null>(null);
  const [threadLoading, setThreadLoading] = useState<boolean>(false);
  const [threadError, setThreadError] = useState<string>("");

  const [replyText, setReplyText] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);
  const [replyError, setReplyError] = useState<string>("");

  const [statusUpdating, setStatusUpdating] = useState<boolean>(false);

  const buildParams = (cursor: string | null) => {
    if (filter === "awaitingReply") {
      return { awaitingReply: true, cursor, limit: 25 };
    }
    return { status: filter, cursor, limit: 25 };
  };

  const loadThreads = async (reset: boolean) => {
    setError("");
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const cursor = reset ? null : nextCursor;
      const data = await fetchFeedback(buildParams(cursor));
      setThreads((prev) => (reset ? data.threads : [...prev, ...data.threads]));
      setNextCursor(data.nextCursor);
      setHasLoaded(true);
    } catch (err: any) {
      const message = err?.response
        ? `Failed to load feedback: ${err.response.status} - ${
            err.response.data?.message || err.response.statusText
          }`
        : `Network error: ${err instanceof Error ? err.message : "Unknown error"}`;
      setError(message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleFilterChange = (value: FilterValue) => {
    if (value === filter) return;
    setFilter(value);
    setThreads([]);
    setNextCursor(null);
    setHasLoaded(false);
    setSelectedThread(null);
    setReplyText("");
    setReplyError("");
  };

  const handleSelectThread = async (thread: FeedbackThread) => {
    setSelectedThread(thread);
    setReplyText("");
    setReplyError("");
    setThreadError("");
    setThreadLoading(true);
    try {
      const fullThread = await fetchFeedbackThread(thread.feedbackId, thread.userEmail);
      setSelectedThread(fullThread);
      // Keep the list row in sync (e.g. ownerHasUnread may change server-side on read)
      setThreads((prev) =>
        prev.map((t) => (t.feedbackId === fullThread.feedbackId ? fullThread : t))
      );
    } catch (err: any) {
      const message = err?.response
        ? `Failed to load thread: ${err.response.status} - ${
            err.response.data?.message || err.response.statusText
          }`
        : `Network error: ${err instanceof Error ? err.message : "Unknown error"}`;
      setThreadError(message);
    } finally {
      setThreadLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selectedThread || !replyText.trim()) return;
    setSending(true);
    setReplyError("");

    try {
      await replyToFeedback(selectedThread.feedbackId, selectedThread.userEmail, replyText.trim());
      setReplyText("");
      const refreshed = await fetchFeedbackThread(
        selectedThread.feedbackId,
        selectedThread.userEmail
      );
      setSelectedThread(refreshed);
      setThreads((prev) =>
        prev.map((t) => (t.feedbackId === refreshed.feedbackId ? refreshed : t))
      );
    } catch (err: any) {
      const message = err?.response
        ? `Failed to send reply: ${err.response.status} - ${
            err.response.data?.message || err.response.statusText
          }`
        : `Network error: ${err instanceof Error ? err.message : "Unknown error"}`;
      setReplyError(message);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status: FeedbackStatus) => {
    if (!selectedThread || selectedThread.status === status) return;
    setStatusUpdating(true);
    setThreadError("");

    try {
      const newStatus = await updateFeedbackStatus(
        selectedThread.feedbackId,
        selectedThread.userEmail,
        status
      );
      const updatedThread: FeedbackThread = { ...selectedThread, status: newStatus };
      setSelectedThread(updatedThread);
      setThreads((prev) =>
        prev.map((t) => (t.feedbackId === updatedThread.feedbackId ? updatedThread : t))
      );
    } catch (err: any) {
      const message = err?.response
        ? `Failed to update status: ${err.response.status} - ${
            err.response.data?.message || err.response.statusText
          }`
        : `Network error: ${err instanceof Error ? err.message : "Unknown error"}`;
      setThreadError(message);
    } finally {
      setStatusUpdating(false);
    }
  };

  return (
    <div className={styles.container}>
      <BackButton />
      <h1 className={styles.title}>Feedback</h1>

      <div className={styles.controls}>
        <div className={styles.filterTabs}>
          {FILTERS.map((f) => (
            <button
              key={f.value}
              className={`${styles.filterTab} ${filter === f.value ? styles.filterTabActive : ""}`}
              onClick={() => handleFilterChange(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => loadThreads(true)}
          className={styles.refreshButton}
          disabled={loading}
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.layout}>
        <div className={styles.listPanel}>
          {loading && threads.length === 0 && (
            <div className={styles.loading}>Loading feedback...</div>
          )}

          {!loading && hasLoaded && threads.length === 0 && (
            <div className={styles.noResults}>No feedback threads found for this filter.</div>
          )}

          {!hasLoaded && !loading && threads.length === 0 && (
            <div className={styles.noResults}>
              Press "Refresh" to load {FILTERS.find((f) => f.value === filter)?.label.toLowerCase()}{" "}
              feedback.
            </div>
          )}

          {threads.length > 0 && (
            <div className={styles.threadList}>
              {threads.map((thread) => (
                <div
                  key={thread.feedbackId}
                  className={`${styles.threadRow} ${
                    selectedThread?.feedbackId === thread.feedbackId ? styles.threadRowActive : ""
                  }`}
                  onClick={() => handleSelectThread(thread)}
                >
                  <div className={styles.threadRowHeader}>
                    <span className={styles.threadDate}>{formatDate(thread.updatedAt)}</span>
                    {thread.ownerHasUnread && (
                      <span className={styles.awaitingBadge} title="Awaiting owner reply">
                        ● Awaiting reply
                      </span>
                    )}
                  </div>
                  <div className={styles.threadRowUser}>
                    <strong>{thread.userName || "Unknown user"}</strong>
                    <span className={styles.threadUserEmail}>{thread.userEmail}</span>
                  </div>
                  <div className={styles.threadRowMeta}>
                    <span
                      className={`${styles.typeBadge} ${
                        thread.type === "skip" ? styles.typeSkip : styles.typeGeneral
                      }`}
                    >
                      {thread.type}
                    </span>
                    <span className={styles.categoryBadge}>{thread.category}</span>
                    <span className={`${styles.statusBadge} ${getStatusClass(thread.status)}`}>
                      {thread.status}
                    </span>
                  </div>
                  <div className={styles.threadPreview}>{getLastMessagePreview(thread)}</div>
                </div>
              ))}
            </div>
          )}

          {nextCursor && (
            <button
              onClick={() => loadThreads(false)}
              className={styles.loadMoreButton}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading..." : "Load more"}
            </button>
          )}
        </div>

        <div className={styles.detailPanel}>
          {!selectedThread && (
            <div className={styles.noSelection}>Select a feedback thread to view details.</div>
          )}

          {selectedThread && (
            <div className={styles.threadDetail}>
              <div className={styles.detailHeader}>
                <div>
                  <h2 className={styles.detailTitle}>
                    {selectedThread.userName || "Unknown user"}
                  </h2>
                  <div className={styles.detailSubtitle}>{selectedThread.userEmail}</div>
                </div>
                <div className={styles.detailBadges}>
                  <span
                    className={`${styles.typeBadge} ${
                      selectedThread.type === "skip" ? styles.typeSkip : styles.typeGeneral
                    }`}
                  >
                    {selectedThread.type}
                  </span>
                  <span className={styles.categoryBadge}>{selectedThread.category}</span>
                  <span
                    className={`${styles.statusBadge} ${getStatusClass(selectedThread.status)}`}
                  >
                    {selectedThread.status}
                  </span>
                </div>
              </div>

              {threadError && <div className={styles.error}>{threadError}</div>}
              {threadLoading && <div className={styles.loading}>Loading thread...</div>}

              <div className={styles.statusActions}>
                {STATUS_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    className={`${styles.statusActionButton} ${
                      selectedThread.status === option.value ? styles.statusActionButtonActive : ""
                    }`}
                    onClick={() => handleStatusChange(option.value)}
                    disabled={statusUpdating || selectedThread.status === option.value}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div className={styles.contextBox}>
                <h3 className={styles.sectionHeading}>Context</h3>
                {selectedThread.context && Object.keys(selectedThread.context).length > 0 ? (
                  <div className={styles.contextGrid}>
                    {Object.entries(selectedThread.context)
                      .filter(([, value]) => value !== undefined && value !== null && value !== "")
                      .map(([key, value]) => (
                        <React.Fragment key={key}>
                          <div className={styles.contextKey}>{key}</div>
                          <div className={styles.contextValue}>
                            {typeof value === "object" ? JSON.stringify(value) : String(value)}
                          </div>
                        </React.Fragment>
                      ))}
                  </div>
                ) : (
                  <div className={styles.noResults}>No context provided.</div>
                )}
              </div>

              <div className={styles.transcript}>
                <h3 className={styles.sectionHeading}>Messages</h3>
                {selectedThread.messages.length === 0 && (
                  <div className={styles.noResults}>No messages yet.</div>
                )}
                {selectedThread.messages.map((message) => (
                  <div
                    key={message.messageId}
                    className={`${styles.message} ${
                      message.author === "owner" ? styles.messageOwner : styles.messageUser
                    }`}
                  >
                    <div className={styles.messageMeta}>
                      <strong>{message.authorName}</strong>
                      <span className={styles.messageDate}>{formatDate(message.createdAt)}</span>
                    </div>
                    <div className={styles.messageText}>{message.text}</div>
                  </div>
                ))}
              </div>

              <div className={styles.replyBox}>
                <h3 className={styles.sectionHeading}>Reply</h3>
                {replyError && <div className={styles.error}>{replyError}</div>}
                <textarea
                  className={styles.replyTextarea}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Write a reply to the user..."
                  rows={4}
                />
                <button
                  className={styles.sendButton}
                  onClick={handleSend}
                  disabled={sending || !replyText.trim()}
                >
                  {sending ? "Sending..." : "Send"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Feedback;
